import { createIntl, createIntlCache, defineMessage, MessageFormatElement } from 'react-intl';
import { parseISO, isValid } from 'date-fns';
import type { Duration, RefundPolicyProps } from '@/types';

type Messages = Record<string, string> | Record<string, MessageFormatElement[]>;

const intlCache = createIntlCache();

const DURATION_DAYS_MESSAGE = defineMessage({
  id: '{days, plural, =1 {# day} other {# days}}',
  defaultMessage: '{days, plural, =1 {# day} other {# days}}',
  description: 'Duration in days with plural support',
});

const DURATION_HOURS_MESSAGE = defineMessage({
  id: '{hours, plural, =1 {# hour} other {# hours}}',
  defaultMessage: '{hours, plural, =1 {# hour} other {# hours}}',
  description: 'Duration in hours with plural support',
});

const DURATION_MINUTES_MESSAGE = defineMessage({
  id: '{minutes, plural, =1 {# minute} other {# minutes}}',
  defaultMessage: '{minutes, plural, =1 {# minute} other {# minutes}}',
  description: 'Duration in minutes with plural support',
});

const REFUND_VALID_DAYS_MESSAGE = defineMessage({
  id: 'Refunds up to {days, plural, =1 {# day} other {# days}} before event',
  defaultMessage: 'Refunds up to {days, plural, =1 {# day} other {# days}} before event',
  description: 'Refund policy message with days count and plural support',
});

const NO_REFUNDS_MESSAGE = defineMessage({
  id: 'No refunds',
  defaultMessage: 'No refunds',
  description: 'Message to display in highlights when no refunds are allowed',
});

const DOOR_TIME_MESSAGE = defineMessage({
  id: 'Doors at {doorTime}',
  defaultMessage: 'Doors at {doorTime}',
  description: 'Message to display in highlights when doors open at a specific time',
});

const getIntl = (locale = 'en', messages: Messages = {}) => {
  return createIntl({ locale, messages, defaultLocale: 'en' }, intlCache);
};

export const formatDuration = (duration: Duration, locale = 'en', messages: Messages = {}): string => {
  const intl = getIntl(locale, messages);

  const daysText = intl.formatMessage(DURATION_DAYS_MESSAGE, { days: duration.days });
  const hoursText = intl.formatMessage(DURATION_HOURS_MESSAGE, { hours: duration.hours });
  const minutesText = intl.formatMessage(DURATION_MINUTES_MESSAGE, { minutes: duration.minutes });

  if (duration.days > 0) {
    if (duration.hours > 0) {
      return `${daysText} ${hoursText}`;
    }
    return daysText;
  }

  if (duration.hours > 0) {
    if (duration.minutes > 0) {
      return `${hoursText} ${minutesText}`;
    }
    return hoursText;
  }

  return minutesText;
};

const getValidDaysText = (validDays: number, locale = 'en', messages: Messages = {}): string => {
  const intl = getIntl(locale, messages);
  return intl.formatMessage(REFUND_VALID_DAYS_MESSAGE, { days: validDays });
};

export const getTranslatedRefundPolicy = (
  refundPolicy: RefundPolicyProps,
  isRepeating = false,
  locale = 'en',
  messages: Messages = {},
): string | null => {
  const { isPaidEvent, validDays, isRefundRequestAllowed, refundPolicyDescription } = refundPolicy;

  if (!isPaidEvent) {
    return null;
  }

  if (isRepeating && validDays) {
    return getValidDaysText(validDays, locale, messages);
  }

  if (!isRefundRequestAllowed) {
    const intl = getIntl(locale, messages);
    return intl.formatMessage(NO_REFUNDS_MESSAGE);
  }

  if (!validDays) {
    return refundPolicyDescription || null;
  }

  return getValidDaysText(validDays, locale, messages);
};

export const getDurationObject = (durationInMinutes: number): Duration => {
  return {
    totalSeconds: durationInMinutes * 60,
    days: Math.floor(durationInMinutes / 1440),
    hours: Math.floor(durationInMinutes / 60) % 24,
    minutes: durationInMinutes % 60,
  };
};

export const formatDoorTime = (doorTime: string, timezone: string, locale = 'en', messages: Messages = {}): string => {
  const intl = getIntl(locale, messages);

  if (!doorTime || typeof doorTime !== 'string' || doorTime.trim() === '') {
    return '';
  }

  let dateObj: Date;
  try {
    dateObj = parseISO(doorTime.trim());

    if (!isValid(dateObj)) {
      return '';
    }
  } catch {
    return '';
  }

  const partsFormatter = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone,
  });

  const parts = partsFormatter.formatToParts(dateObj);
  const hour = parts.find((part) => part.type === 'hour')?.value;
  const minute = parts.find((part) => part.type === 'minute')?.value;
  const dayPeriod = parts.find((part) => part.type === 'dayPeriod')?.value;

  if (!hour || !minute || !dayPeriod) {
    const fallbackFormatter = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: timezone,
    });
    const formattedTime = fallbackFormatter.format(dateObj);
    return intl.formatMessage(DOOR_TIME_MESSAGE, { doorTime: formattedTime });
  }

  const formattedTime = minute === '00' ? `${hour}${dayPeriod}` : `${hour}:${minute} ${dayPeriod}`;

  return intl.formatMessage(DOOR_TIME_MESSAGE, { doorTime: formattedTime });
};

export const isNotValidDuration = (durationInMinutes: number): boolean => {
  return durationInMinutes <= 0 || isNaN(durationInMinutes);
};
